import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarDays, Loader2, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api } from '../../lib/api'
import { currentLocaleTag } from '../../lib/formatting'
import {
  CalendarEventTypeColor,
  type CalendarEventDto,
  type CalendarListResponse,
} from '@jr2os/shared'
import { addDays, startOfDay } from '../../lib/datetime'

export default function UpcomingEventsWidget() {
  const { t } = useTranslation('agenda')
  const [events, setEvents] = useState<CalendarEventDto[] | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const from = startOfDay(new Date())
        const to = addDays(from, 7)
        const params = new URLSearchParams({
          from: from.toISOString(),
          to: to.toISOString(),
        })
        const resp = await api.get<CalendarListResponse>(`/calendar-events?${params.toString()}`)
        setEvents(resp.items)
      } catch {
        setEvents([])
      }
    })()
  }, [])

  return (
    <div className="surface p-5">
      <h3 className="t-primary font-semibold mb-3 flex items-center justify-between">
        <span className="flex items-center gap-2">
          <CalendarDays size={16} className="text-jr2-700 dark:text-jr2-300" />
          {t('upcoming.title')}
        </span>
        <Link to="/agenda" className="text-xs text-jr2-700 dark:text-jr2-300 hover:underline">
          {t('upcoming.viewAgenda')}
        </Link>
      </h3>

      {!events ? (
        <div className="t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> {t('upcoming.loading')}
        </div>
      ) : events.length === 0 ? (
        <p className="t-muted text-sm">{t('upcoming.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {events.slice(0, 6).map((e) => {
            const color = CalendarEventTypeColor[e.type]
            const start = new Date(e.startAt)
            return (
              <li key={e.id}>
                <Link
                  to="/agenda"
                  className="flex items-center gap-3 px-1 py-1.5 rounded-lg hover:bg-hover/50"
                >
                  <span
                    className={`w-10 text-center text-[10px] uppercase tracking-wider px-1 py-1 rounded ${color.bg} ${color.text} ${color.border} border`}
                  >
                    {start.toLocaleDateString(currentLocaleTag(), {
                      day: '2-digit',
                      month: 'short',
                    })}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm t-primary truncate">{e.title}</div>
                    <div className="text-xs t-faint truncate">
                      {!e.allDay &&
                        start.toLocaleTimeString(currentLocaleTag(), {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                      {e.opportunityCode && `· ${e.opportunityCode}`} · {t('enums:CalendarEventType.' + e.type)}
                    </div>
                  </div>
                  <ChevronRight size={14} className="t-faint" />
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
